import { type ChangeEventHandler, type FormEvent, useState } from 'react'
import { useCookies } from 'react-cookie'
import styled from 'styled-components'

import { Button } from '@/components/shared/Button'
import { CheckMark } from '@/components/shared/icons/CheckMark'
import { type StandardButtonProps, StandardButton } from '@/components/shared/StandardButton'
import { TrackedLink } from '@/components/shared/TrackedLink'

import { GUTSHOT_EMAIL_SUBMITTED } from '@/lib/braze/brazeConstants'
import { registerBrazeUserWithEmailFromWebForm } from '@/lib/braze/brazeUtils'
import { QUIZ_ID_COOKIE_NAME } from '@/lib/checkoutConstants'
import { trackCtaClick, trackSignupEvent } from '@/lib/gtm/gtmV2'
import { useCheckoutCountry } from '@/lib/hooks/useCheckoutCountry'

const StyledContainer = styled.div`
  flex: 1;
  .message-text {
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueLight};
    font-size: ${(p) => p.theme.v2.typography.body.m.fontSize};
    line-height: ${(p) => p.theme.v2.typography.body.m.lineHeight};
  }
  .message-header {
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueRegular};
    font-size: ${(p) => p.theme.v2.typography.body.l.fontSize};
    line-height: ${(p) => p.theme.v2.typography.body.l.lineHeight};
    margin: 16px 0;
    @media screen and (max-width: ${(p) => p.theme.breakpoints.tablet}) {
      font-size: ${(p) => p.theme.v2.typography.body.m.fontSize};
      line-height: ${(p) => p.theme.v2.typography.body.m.lineHeight};
    }
    @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
      font-size: ${(p) => p.theme.v2.typography.body.l.fontSize};
      line-height: ${(p) => p.theme.v2.typography.body.l.lineHeight};
    }
  }
  small {
    font-size: 12px;
    margin-top: 12px;
    display: block;
  }
  a.primary {
    max-width: 300px;
    padding: 18px 32px;
  }
`

const StyledSuccessMessageContainer = styled.div<StyledSuccessMessageContainerProps>`
  display: flex;
  flex-direction: column;
  max-width: 508px;
  text-align: ${(p) => p.textAlgin};
  align-items: ${(p) => p.textAlgin};
  gap: 16px;
`

const StyledSuccessMessageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  gap: 8px;
  @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    margin-top: 24px;
  }
`

const StyledErrorMessageContainer = styled.div<StyledErrorMessageContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => p.textAlgin};
  text-align: ${(p) => p.textAlgin};
  gap: 8px;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: row;
  gap: 16px;
  @media screen and (max-width: ${(p) => p.theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: 12px;
  }
`

const StyledField = styled.div`
  flex: 0 0 calc(50% - 12px);

  @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    flex: 0 0 100%;
  }

  label {
    display: block;
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueLight};
    font-size: ${(p) => p.theme.v2.typography.body.xxs.fontSize};
    line-height: ${(p) => p.theme.v2.typography.body.xxs.lineHeight};
    margin-bottom: 5px;
    text-align: left;
  }

  input {
    border: 1px solid ${(p) => p.theme.colours.separatorBorder};
    border-radius: 200px;
    border-color: #2d3246;
    height: 64px;
    padding: 0 16px;
    width: 100%;
  }
`

const StyledButton = styled.div`
  flex: 0 0 calc(50% - 12px);
  margin: 0;
  align-self: center;
  width: 100%;
  #email-submit {
    height: 64px;
    padding: 0 18px;
    align-items: center;
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoints.tablet}) {
    flex: 0 0 100%;
    margin: unset;
  }
`

type EmailSubscribeWidgetProps = {
  includePrivacyPolicy?: boolean
  includeAgreeToNewsletterSignup?: boolean
  buttonStyle: StandardButtonProps['btnStyle']
  postSignupMessageStyle: 'center' | 'left'
}

interface StyledErrorMessageContainerProps {
  textAlgin: 'center' | 'left'
}
interface StyledSuccessMessageContainerProps {
  textAlgin: 'center' | 'left'
}

const handleOnClick = (label: string) => {
  trackCtaClick('gut_shot', label)
}

export const EmailSubscribeWidget = ({
  includePrivacyPolicy,
  buttonStyle,
  postSignupMessageStyle,
}: EmailSubscribeWidgetProps) => {
  const [postSignupMessage, setPostSignupMessage] = useState({ header: '', tagline: '' })
  const [error, setError] = useState(false)
  const [email, setEmail] = useState('')
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [cookies] = useCookies([QUIZ_ID_COOKIE_NAME])
  const { isReady, geoLocCountry } = useCheckoutCountry()

  const handleEmailChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newEmail = event.target.value
    setEmail(newEmail)
    validateForm(newEmail)
  }

  const validateForm = (newEmail: string) => {
    const isValidForm = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)
    setIsValid(isValidForm)
  }

  const subscribeCallback = (success: boolean) => {
    setIsSubscribed(success)

    if (success) {
      setPostSignupMessage({
        header: 'Thanks for signing up!',
        tagline:
          'The first email will arrive soon. In the meantime, why not try one of our delicious gut-friendly recipes?',
      })
    } else {
      setPostSignupMessage({
        header: 'Something went wrong 😢',
        tagline: 'Please refresh the page and try again.',
      })
      setError(true)
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    if (email) {
      const marketingConsent = true
      const additionalFields = {
        ...(cookies.cUserId && { quizUserId: cookies.cUserId }),
        ...(isReady && geoLocCountry && { country: geoLocCountry }),
      }
      await registerBrazeUserWithEmailFromWebForm(
        e,
        email,
        GUTSHOT_EMAIL_SUBMITTED,
        subscribeCallback,
        marketingConsent,
        Object.keys(additionalFields).length !== 0 ? additionalFields : undefined,
      )
    }

    trackSignupEvent({
      email,
      section: 'gut_shot',
      emailList: 'gut_health_guide',
    })
  }

  return (
    <StyledContainer>
      {!isSubscribed && (
        <>
          <StyledForm method="post" onSubmit={handleSubmit}>
            <StyledField>
              <label htmlFor="email-input"></label>
              <input
                id="email-input"
                name="email"
                required
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Your email"
              />
            </StyledField>

            <StyledButton>
              <StandardButton
                id="email-submit"
                type="submit"
                withArrow
                disabled={!isValid}
                btnStyle={buttonStyle}
                size="full-width"
                onClick={() => handleOnClick('Get your free guide')}
              >
                Get your free guide
              </StandardButton>
            </StyledButton>
          </StyledForm>
          {includePrivacyPolicy && (
            <small>
              Read our{' '}
              <TrackedLink href="/privacy-policy" target="_blank" rel="noreferrer">
                Privacy Policy
              </TrackedLink>{' '}
              to learn how we use your information. You can unsubscribe anytime.
            </small>
          )}
        </>
      )}
      {isSubscribed && (
        <StyledSuccessMessageContainer textAlgin={postSignupMessageStyle}>
          <StyledSuccessMessageHeader>
            <CheckMark colour={buttonStyle === 'primary' ? '#FFD100' : 'white'} />
            <h3 className="message-header">{postSignupMessage.header}</h3>
          </StyledSuccessMessageHeader>
          <span className="message-text">{postSignupMessage.tagline}</span>
          <Button
            url="https://storage.googleapis.com/zoe-nutrition-email-campaigns/Gut-Health-Guide-29-12-23.pdf"
            text="Get gut-friendly recipes"
            withArrow
            style={buttonStyle}
            target="_blank"
            onClick={() => handleOnClick('Get gut-friendly recipes')}
          />
        </StyledSuccessMessageContainer>
      )}
      {error && (
        <StyledErrorMessageContainer textAlgin={postSignupMessageStyle}>
          <h3 className="message-header">{postSignupMessage.header}</h3>
          <span className="message-text">{postSignupMessage.tagline}</span>
        </StyledErrorMessageContainer>
      )}
    </StyledContainer>
  )
}
